import { useMemo, useState } from 'react';
import {
  ArrowLeft,
  Bot,
  ChevronRight,
  Database,
  FileText,
  GitBranch,
  KeyRound,
  Layers,
  Lock,
  Network,
  Search,
  Server,
  Shield,
  Sparkles,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../layout/Header';
import { bzDatabaseAtlasSummary, bzDatabaseDomains, bzDatabaseRelations, bzDatabaseTables } from '../data/bzDatabaseAtlasData';

type AccessLevel = 'Élevée' | 'Moyenne' | 'À qualifier';
type AccessMode = 'Lecture seule' | 'Lecture + écriture contrôlée' | 'Restreint / audit obligatoire';
type ViewMode = 'domaines' | 'tables';

type ParsedRelation = {
  source: string;
  target: string;
  label: string;
  raw: string;
};

type GraphNode = {
  id: string;
  label: string;
  domain: string;
  x: number;
  y: number;
  radius: number;
  tableCount?: number;
  columnCount?: number;
};

const targetDomains = [
  'Identité & accès',
  'Clients & CRM',
  'Comptabilité / ventes',
  'Tickets TI',
  'Sécurité & conformité',
  'Documentation',
  'Licences & contrats',
  'Télécom BZT',
  'RH',
  'Dépenses',
];

const premiumPrinciples = [
  {
    title: 'Voir toute la surface',
    text: 'Un vrai cockpit de données doit utiliser toute la largeur : inventaire, map, inspector et contrats d’accès dans la même lecture.',
    icon: Network,
  },
  {
    title: 'Documenter avant automatiser',
    text: 'Chaque table doit avoir un usage, un propriétaire, un accès DB direct et une façade API cible avant d’être exposée à des bots.',
    icon: FileText,
  },
  {
    title: 'Sécurité by-design',
    text: 'Les domaines sensibles — clients, finances, accès, sécurité, RH — doivent partir en lecture gouvernée avant toute écriture agentique.',
    icon: Shield,
  },
  {
    title: 'Scalable client',
    text: 'Le modèle doit devenir réutilisable : BZ aujourd’hui, puis même méthode d’audit visuel pour les clients.',
    icon: Sparkles,
  },
];

function formatNumber(value: number) {
  return new Intl.NumberFormat('fr-CA').format(value);
}

function parseRelation(raw: string): ParsedRelation | null {
  const match = raw.match(/^\s*([^\s]+)\s+--[^>]*>\s+([^\s:]+)\s*(?::\s*(.*))?$/);
  if (!match) return null;
  return {
    source: match[1],
    target: match[2],
    label: match[3] || 'relation',
    raw,
  };
}

function normalizeSlug(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function tableByName(name: string) {
  return bzDatabaseTables.find((table) => table.name === name);
}

function sensitivityFor(domain: string): AccessLevel {
  if (/Identités|Sécurité|Comptabilité|Dépenses|Clients|RH/.test(domain)) return 'Élevée';
  if (/Documentation|Licences|Contrats|Tickets|Télécom|Ventes|Inventaire/.test(domain)) return 'Moyenne';
  return 'À qualifier';
}

function accessModeFor(domain: string): AccessMode {
  const sensitivity = sensitivityFor(domain);
  if (sensitivity === 'Élevée') return 'Restreint / audit obligatoire';
  if (sensitivity === 'Moyenne') return 'Lecture + écriture contrôlée';
  return 'Lecture seule';
}

function dbAccessFor(domain: string, tableName?: string) {
  const slug = normalizeSlug(domain);
  const table = tableName || '<table>';
  return {
    directDb: `bzinc.${table}`,
    readRole: `bz_${slug}_read`,
    writeRole: sensitivityFor(domain) === 'Élevée' ? `bz_${slug}_write_approved` : `bz_${slug}_write`,
    apiBase: `/api/data-atlas/${slug}`,
    apiTable: `/api/data-atlas/${slug}/${table}`,
    policy: accessModeFor(domain),
  };
}

function sensitivityClass(value: AccessLevel) {
  if (value === 'Élevée') return 'border-red-200 bg-red-50 text-red-700';
  if (value === 'Moyenne') return 'border-amber-200 bg-amber-50 text-amber-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
}

function buildDomainGraph(selectedDomain: string): { nodes: GraphNode[]; edges: Array<{ source: string; target: string; weight: number }> } {
  const topDomains = bzDatabaseDomains.slice(0, 14);
  const centerX = 420;
  const centerY = 255;
  const radius = 205;
  const nodes = topDomains.map((domain, index) => {
    const angle = (Math.PI * 2 * index) / topDomains.length - Math.PI / 2;
    const selected = selectedDomain === domain.name;
    return {
      id: domain.name,
      label: domain.name,
      domain: domain.name,
      x: centerX + Math.cos(angle) * radius,
      y: centerY + Math.sin(angle) * radius,
      radius: selected ? 28 : Math.max(15, Math.min(26, 12 + domain.tableCount / 6)),
      tableCount: domain.tableCount,
      columnCount: domain.columnCount,
    };
  });

  const known = new Set(nodes.map((node) => node.id));
  const edgeCount = new Map<string, number>();
  bzDatabaseRelations
    .map(parseRelation)
    .filter(Boolean)
    .forEach((relation) => {
      const sourceDomain = tableByName(relation!.source)?.domain;
      const targetDomain = tableByName(relation!.target)?.domain;
      if (!sourceDomain || !targetDomain || sourceDomain === targetDomain || !known.has(sourceDomain) || !known.has(targetDomain)) return;
      const key = [sourceDomain, targetDomain].sort().join('→');
      edgeCount.set(key, (edgeCount.get(key) || 0) + 1);
    });

  const edges = Array.from(edgeCount.entries()).map(([key, weight]) => {
    const [source, target] = key.split('→');
    return { source, target, weight };
  });

  return { nodes, edges };
}

function buildTableGraph(domainName: string, selectedTable: string): { nodes: GraphNode[]; edges: ParsedRelation[] } {
  const domainTables = bzDatabaseTables.filter((table) => table.domain === domainName);
  const parsed = bzDatabaseRelations.map(parseRelation).filter(Boolean) as ParsedRelation[];
  const connectedNames = new Set<string>();
  parsed.forEach((relation) => {
    const sourceDomain = tableByName(relation.source)?.domain;
    const targetDomain = tableByName(relation.target)?.domain;
    if (sourceDomain === domainName || targetDomain === domainName) {
      connectedNames.add(relation.source);
      connectedNames.add(relation.target);
    }
  });

  const important = domainTables
    .filter((table) => connectedNames.has(table.name) || table.name === selectedTable)
    .sort((a, b) => b.columnCount - a.columnCount)
    .slice(0, 22);
  const fallback = important.length > 0 ? important : domainTables.slice(0, 22);
  const centerX = 440;
  const centerY = 265;
  const radius = 215;
  const nodes = fallback.map((table, index) => {
    const angle = (Math.PI * 2 * index) / Math.max(1, fallback.length) - Math.PI / 2;
    const isSelected = selectedTable === table.name;
    return {
      id: table.name,
      label: table.name,
      domain: table.domain,
      x: centerX + Math.cos(angle) * radius,
      y: centerY + Math.sin(angle) * radius,
      radius: isSelected ? 22 : Math.max(10, Math.min(18, 8 + table.columnCount / 10)),
      columnCount: table.columnCount,
    };
  });
  const names = new Set(nodes.map((node) => node.id));
  const edges = parsed.filter((relation) => names.has(relation.source) && names.has(relation.target)).slice(0, 45);
  return { nodes, edges };
}

export default function AtlasDonneesBZ() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('Clients & CRM');
  const [selectedTable, setSelectedTable] = useState('clients');
  const [viewMode, setViewMode] = useState<ViewMode>('domaines');

  const parsedRelations = useMemo(() => bzDatabaseRelations.map(parseRelation).filter(Boolean) as ParsedRelation[], []);

  const filteredTables = useMemo(() => {
    const q = search.trim().toLowerCase();
    return bzDatabaseTables.filter((table) => {
      const domainMatches = selectedDomain === 'Tous' || table.domain === selectedDomain;
      const searchMatches =
        !q ||
        table.name.toLowerCase().includes(q) ||
        table.domain.toLowerCase().includes(q) ||
        table.columns.some((column) => column.toLowerCase().includes(q));
      return domainMatches && searchMatches;
    });
  }, [search, selectedDomain]);

  const activeTable = useMemo(
    () => bzDatabaseTables.find((table) => table.name === selectedTable) || filteredTables[0] || bzDatabaseTables[0],
    [filteredTables, selectedTable],
  );
  const activeDomain = bzDatabaseDomains.find((domain) => domain.name === selectedDomain) || bzDatabaseDomains[0];
  const access = dbAccessFor(activeTable?.domain || activeDomain.name, activeTable?.name);
  const tableRelations = parsedRelations.filter((relation) => relation.source === activeTable?.name || relation.target === activeTable?.name);
  const domainGraph = useMemo(() => buildDomainGraph(selectedDomain), [selectedDomain]);
  const tableGraph = useMemo(() => buildTableGraph(activeDomain.name, activeTable?.name || ''), [activeDomain.name, activeTable?.name]);
  const currentGraph = viewMode === 'domaines' ? domainGraph : tableGraph;

  function selectDomain(domain: string) {
    setSelectedDomain(domain);
    const firstTable = bzDatabaseTables.find((table) => table.domain === domain);
    if (firstTable) setSelectedTable(firstTable.name);
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Header
        title="Atlas des données BZ"
        subtitle="Workspace premium pour documenter les DB, tables, accès directs, API et frontières sécurité-by-design."
      />

      <main className="w-full px-4 pb-8 sm:px-6 lg:px-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate('/ia-agentic')}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-white/15"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour IA agentic
          </button>
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-200">
            <span className="rounded-full border border-cyan-300/25 bg-cyan-300/10 px-3 py-1">Version premium UX</span>
            <span className="rounded-full border border-emerald-300/25 bg-emerald-300/10 px-3 py-1">Frontend only</span>
            <span className="rounded-full border border-purple-300/25 bg-purple-300/10 px-3 py-1">Référentiel client-ready</span>
          </div>
        </div>

        <section className="grid min-h-[calc(100vh-150px)] overflow-hidden rounded-[2rem] border border-white/10 bg-white text-slate-900 shadow-2xl lg:grid-cols-[320px_minmax(0,1fr)_390px]">
          <aside className="border-r border-slate-200 bg-slate-50 p-5">
            <div className="rounded-3xl bg-gradient-to-br from-slate-950 via-blue-950 to-cyan-900 p-5 text-white shadow-xl">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-cyan-100">
                <Database className="h-4 w-4" />
                {bzDatabaseAtlasSummary.recommendedName}
              </div>
              <h1 className="text-2xl font-black leading-tight">La carte officielle des données BZ.</h1>
              <p className="mt-3 text-sm leading-6 text-blue-100">
                De l’inventaire brut vers une gouvernance lisible : domaines, tables, relations, accès DB et futures API.
              </p>
              <div className="mt-5 grid grid-cols-2 gap-2">
                <Metric label="Tables" value={bzDatabaseAtlasSummary.tableCount} />
                <Metric label="Colonnes" value={bzDatabaseAtlasSummary.columnCount} />
                <Metric label="Domaines" value={bzDatabaseAtlasSummary.domainCount} />
                <Metric label="Relations" value={bzDatabaseAtlasSummary.relationCount} />
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-black text-slate-900">Domaines</h2>
                <Layers className="h-4 w-4 text-blue-600" />
              </div>
              <div className="max-h-[48vh] space-y-2 overflow-auto pr-1">
                {bzDatabaseDomains.slice(0, 18).map((domain) => {
                  const sensitivity = sensitivityFor(domain.name);
                  return (
                    <button
                      key={domain.name}
                      type="button"
                      onClick={() => selectDomain(domain.name)}
                      className={`w-full rounded-2xl border px-3 py-3 text-left transition hover:border-blue-300 hover:bg-blue-50 ${selectedDomain === domain.name ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-slate-200 bg-white'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-black text-slate-900">{domain.name}</span>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${sensitivityClass(sensitivity)}`}>{sensitivity}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
                        <span>{domain.tableCount} tables</span>
                        <span>{formatNumber(domain.columnCount)} colonnes</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          <section className="min-w-0 bg-gradient-to-br from-slate-50 via-white to-blue-50 p-5">
            <div className="mb-5 grid gap-3 xl:grid-cols-4">
              {premiumPrinciples.map((principle) => {
                const Icon = principle.icon;
                return (
                  <article key={principle.title} className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-sm font-black text-slate-900">{principle.title}</h3>
                        <p className="mt-1 text-xs leading-5 text-slate-600">{principle.text}</p>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-lg">
              <div className="mb-4 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                <div>
                  <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-slate-600">
                    <Network className="h-4 w-4" />
                    Graph map interactive
                  </div>
                  <h2 className="text-2xl font-black text-slate-950">Map des domaines et relations</h2>
                  <p className="mt-1 text-sm text-slate-600">
                    Clique un domaine ou une table : l’inspecteur à droite affiche l’information, les accès DB directs et la façade API cible.
                  </p>
                </div>
                <div className="flex rounded-2xl border border-slate-200 bg-slate-50 p-1">
                  <button
                    type="button"
                    onClick={() => setViewMode('domaines')}
                    className={`rounded-xl px-4 py-2 text-sm font-bold transition ${viewMode === 'domaines' ? 'bg-slate-950 text-white shadow' : 'text-slate-600 hover:bg-white'}`}
                  >
                    Domaines
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('tables')}
                    className={`rounded-xl px-4 py-2 text-sm font-bold transition ${viewMode === 'tables' ? 'bg-slate-950 text-white shadow' : 'text-slate-600 hover:bg-white'}`}
                  >
                    Tables liées
                  </button>
                </div>
              </div>

              <GraphCanvas
                mode={viewMode}
                nodes={currentGraph.nodes}
                domainEdges={'weight' in (currentGraph.edges[0] || {}) ? (currentGraph.edges as Array<{ source: string; target: string; weight: number }>) : []}
                tableEdges={'raw' in (currentGraph.edges[0] || {}) ? (currentGraph.edges as ParsedRelation[]) : []}
                selectedDomain={selectedDomain}
                selectedTable={activeTable?.name || ''}
                onSelectDomain={selectDomain}
                onSelectTable={(name) => {
                  const table = tableByName(name);
                  if (table) {
                    setSelectedDomain(table.domain);
                    setSelectedTable(name);
                  }
                }}
              />
            </div>

            <div className="mt-5 grid gap-5 xl:grid-cols-[minmax(0,1.15fr)_minmax(420px,0.85fr)]">
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-lg">
                <div className="mb-4 flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
                  <div>
                    <h2 className="text-xl font-black text-slate-950">Catalogue des tables</h2>
                    <p className="text-sm text-slate-500">Recherche par table, domaine ou colonne. Sélection synchronisée avec la map.</p>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <label className="relative block">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Rechercher table, colonne..."
                        className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
                      />
                    </label>
                    <select
                      value={selectedDomain}
                      onChange={(event) => selectDomain(event.target.value)}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
                    >
                      {bzDatabaseDomains.map((domain) => (
                        <option key={domain.name}>{domain.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="max-h-[430px] overflow-auto rounded-2xl border border-slate-200">
                  <table className="w-full border-collapse text-sm">
                    <thead className="sticky top-0 z-10 bg-slate-950 text-left text-xs uppercase tracking-[0.14em] text-white">
                      <tr>
                        <th className="px-4 py-3">Table</th>
                        <th className="px-4 py-3">Domaine</th>
                        <th className="px-4 py-3">Accès DB direct</th>
                        <th className="px-4 py-3">Accès API cible</th>
                        <th className="px-4 py-3">Risque</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTables.slice(0, 220).map((table) => {
                        const rowAccess = dbAccessFor(table.domain, table.name);
                        const sensitivity = sensitivityFor(table.domain);
                        return (
                          <tr
                            key={table.name}
                            onClick={() => setSelectedTable(table.name)}
                            className={`cursor-pointer border-b border-slate-100 transition hover:bg-blue-50 ${activeTable?.name === table.name ? 'bg-blue-50' : 'bg-white'}`}
                          >
                            <td className="px-4 py-3 font-black text-slate-900">{table.name}</td>
                            <td className="px-4 py-3 text-slate-600">{table.domain}</td>
                            <td className="px-4 py-3"><code className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-700">{rowAccess.directDb}</code></td>
                            <td className="px-4 py-3"><code className="rounded bg-blue-50 px-2 py-1 text-xs text-blue-700">{rowAccess.apiTable}</code></td>
                            <td className="px-4 py-3"><span className={`rounded-full border px-2 py-1 text-xs font-bold ${sensitivityClass(sensitivity)}`}>{sensitivity}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {filteredTables.length > 220 && (
                    <div className="bg-slate-50 px-4 py-3 text-xs text-slate-500">{formatNumber(filteredTables.length - 220)} autres résultats masqués — raffine la recherche.</div>
                  )}
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-lg">
                <h2 className="mb-4 flex items-center gap-2 text-xl font-black text-slate-950">
                  <GitBranch className="h-5 w-5 text-blue-600" />
                  Découpage cible premium
                </h2>
                <div className="grid gap-2 sm:grid-cols-2">
                  {targetDomains.map((domain) => (
                    <div key={domain} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                      <div className="font-bold text-slate-900">{domain}</div>
                      <div className="mt-1 text-xs text-slate-500">DB/API domaine gouverné</div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                  <strong>Recommandation architecture :</strong> ne pas casser la DB d’un coup. Créer d’abord des façades API par domaine, droits DB minimaux, journaux d’accès, puis migrer physiquement les domaines les plus sensibles.
                </div>
              </div>
            </div>
          </section>

          <aside className="border-l border-slate-200 bg-white p-5">
            <div className="sticky top-4 space-y-5">
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-600">Inspecteur</p>
                    <h2 className="mt-1 break-all text-2xl font-black text-slate-950">{activeTable?.name}</h2>
                    <p className="mt-1 text-sm text-slate-500">{activeTable?.domain}</p>
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-xs font-bold ${sensitivityClass(sensitivityFor(activeTable?.domain || activeDomain.name))}`}>
                    {sensitivityFor(activeTable?.domain || activeDomain.name)}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <InfoTile label="Colonnes" value={String(activeTable?.columnCount || 0)} />
                  <InfoTile label="Relations" value={String(tableRelations.length)} />
                  <InfoTile label="Politique" value={access.policy} wide />
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="mb-3 flex items-center gap-2 font-black text-slate-950"><Server className="h-5 w-5 text-blue-600" /> Accès direct DB</h3>
                <AccessLine label="Objet" value={access.directDb} />
                <AccessLine label="Rôle lecture" value={access.readRole} />
                <AccessLine label="Rôle écriture" value={access.writeRole} />
                <p className="mt-3 rounded-2xl bg-red-50 p-3 text-xs leading-5 text-red-700">
                  Accès direct réservé aux jobs internes, migrations et diagnostics. Les bots clients devraient passer par API gouvernée, pas par table brute.
                </p>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="mb-3 flex items-center gap-2 font-black text-slate-950"><Bot className="h-5 w-5 text-blue-600" /> Accès via API</h3>
                <AccessLine label="Domaine" value={access.apiBase} />
                <AccessLine label="Table" value={access.apiTable} />
                <AccessLine label="Mode bot" value={access.policy} />
                <p className="mt-3 rounded-2xl bg-blue-50 p-3 text-xs leading-5 text-blue-800">
                  API cible à documenter : auth, scopes, lecture/écriture, audit log, rate limit, masquage PII et justification métier.
                </p>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="mb-3 flex items-center gap-2 font-black text-slate-950"><KeyRound className="h-5 w-5 text-blue-600" /> Colonnes</h3>
                <div className="max-h-[250px] overflow-auto rounded-2xl bg-slate-50 p-3">
                  <div className="grid gap-2">
                    {activeTable?.columns.map((column) => (
                      <div key={`${activeTable.name}-${column}`} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700">
                        {column}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="mb-3 flex items-center gap-2 font-black text-slate-950"><Lock className="h-5 w-5 text-blue-600" /> Relations directes</h3>
                <div className="max-h-[180px] space-y-2 overflow-auto">
                  {tableRelations.slice(0, 12).map((relation) => (
                    <button
                      key={relation.raw}
                      type="button"
                      onClick={() => {
                        const next = relation.source === activeTable?.name ? relation.target : relation.source;
                        const nextTable = tableByName(next);
                        if (nextTable) {
                          setSelectedDomain(nextTable.domain);
                          setSelectedTable(nextTable.name);
                          setViewMode('tables');
                        }
                      }}
                      className="flex w-full items-center justify-between gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-left text-xs text-slate-700 transition hover:border-blue-300 hover:bg-blue-50"
                    >
                      <span className="truncate">{relation.source} → {relation.target}</span>
                      <ChevronRight className="h-3 w-3 shrink-0" />
                    </button>
                  ))}
                  {tableRelations.length === 0 && <p className="text-xs text-slate-500">Aucune relation directe dans le MD pour cette table.</p>}
                </div>
              </div>
            </div>
          </aside>
        </section>
      </main>
    </div>
  );
}

function GraphCanvas({
  mode,
  nodes,
  domainEdges,
  tableEdges,
  selectedDomain,
  selectedTable,
  onSelectDomain,
  onSelectTable,
}: {
  mode: ViewMode;
  nodes: GraphNode[];
  domainEdges: Array<{ source: string; target: string; weight: number }>;
  tableEdges: ParsedRelation[];
  selectedDomain: string;
  selectedTable: string;
  onSelectDomain: (domain: string) => void;
  onSelectTable: (table: string) => void;
}) {
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  return (
    <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-slate-950 shadow-inner">
      <div className="absolute left-5 top-5 z-10 rounded-2xl border border-white/10 bg-white/10 px-4 py-3 text-white backdrop-blur">
        <div className="text-xs font-bold uppercase tracking-[0.16em] text-cyan-200">Vue active</div>
        <div className="mt-1 text-lg font-black">{mode === 'domaines' ? selectedDomain : selectedTable}</div>
      </div>
      <svg viewBox="0 0 880 540" className="h-[540px] w-full">
        <defs>
          <radialGradient id="atlasGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#1d4ed8" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#020617" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect width="880" height="540" fill="#020617" />
        <circle cx="440" cy="270" r="250" fill="url(#atlasGlow)" />
        <g opacity="0.22">
          {Array.from({ length: 9 }).map((_, index) => (
            <circle key={index} cx="440" cy="270" r={55 + index * 28} fill="none" stroke="#67e8f9" strokeWidth="0.7" />
          ))}
        </g>

        {mode === 'domaines' && domainEdges.map((edge) => {
          const source = nodeMap.get(edge.source);
          const target = nodeMap.get(edge.target);
          if (!source || !target) return null;
          const active = source.id === selectedDomain || target.id === selectedDomain;
          return (
            <line
              key={`${edge.source}-${edge.target}`}
              x1={source.x}
              y1={source.y}
              x2={target.x}
              y2={target.y}
              stroke={active ? '#22d3ee' : '#64748b'}
              strokeWidth={active ? Math.min(7, 1.5 + edge.weight / 2) : Math.min(4, 0.8 + edge.weight / 3)}
              opacity={active ? 0.72 : 0.26}
            />
          );
        })}

        {mode === 'tables' && tableEdges.map((edge) => {
          const source = nodeMap.get(edge.source);
          const target = nodeMap.get(edge.target);
          if (!source || !target) return null;
          const active = source.id === selectedTable || target.id === selectedTable;
          return (
            <line
              key={edge.raw}
              x1={source.x}
              y1={source.y}
              x2={target.x}
              y2={target.y}
              stroke={active ? '#38bdf8' : '#94a3b8'}
              strokeWidth={active ? 3.2 : 1.2}
              opacity={active ? 0.84 : 0.28}
            />
          );
        })}

        {nodes.map((node) => {
          const active = mode === 'domaines' ? node.id === selectedDomain : node.id === selectedTable;
          const fill = active ? '#22d3ee' : sensitivityFor(node.domain) === 'Élevée' ? '#ef4444' : sensitivityFor(node.domain) === 'Moyenne' ? '#f59e0b' : '#60a5fa';
          return (
            <g
              key={node.id}
              role="button"
              tabIndex={0}
              onClick={() => (mode === 'domaines' ? onSelectDomain(node.id) : onSelectTable(node.id))}
              className="cursor-pointer"
            >
              <circle cx={node.x} cy={node.y} r={node.radius + 8} fill={fill} opacity={active ? 0.2 : 0.08} />
              <circle cx={node.x} cy={node.y} r={node.radius} fill={fill} stroke={active ? '#ffffff' : '#0f172a'} strokeWidth={active ? 4 : 2} />
              <text x={node.x} y={node.y + node.radius + 18} textAnchor="middle" fill="#e2e8f0" fontSize={mode === 'domaines' ? 12 : 10} fontWeight="700">
                {node.label.length > 24 ? `${node.label.slice(0, 22)}…` : node.label}
              </text>
              <text x={node.x} y={node.y + 4} textAnchor="middle" fill="#ffffff" fontSize="11" fontWeight="900">
                {mode === 'domaines' ? node.tableCount : node.columnCount}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="absolute bottom-5 left-5 right-5 grid gap-2 rounded-2xl border border-white/10 bg-white/10 p-3 text-xs text-slate-200 backdrop-blur md:grid-cols-3">
        <span><strong className="text-cyan-200">Rouge</strong> : données sensibles / accès restreint</span>
        <span><strong className="text-amber-200">Orange</strong> : écriture contrôlée</span>
        <span><strong className="text-blue-200">Bleu</strong> : à qualifier / lecture d’abord</span>
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur">
      <div className="text-xl font-black text-white">{formatNumber(value)}</div>
      <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-blue-100">{label}</div>
    </div>
  );
}

function InfoTile({ label, value, wide = false }: { label: string; value: string; wide?: boolean }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-3 ${wide ? 'col-span-2' : ''}`}>
      <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">{label}</div>
      <div className="mt-1 text-sm font-black text-slate-900">{value}</div>
    </div>
  );
}

function AccessLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="mb-2 rounded-2xl border border-slate-200 bg-slate-50 p-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">{label}</div>
      <code className="mt-1 block break-all text-xs font-semibold text-slate-800">{value}</code>
    </div>
  );
}
